import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/context/LanguageContext";
import { AccessibilityProvider } from "@/context/AccessibilityContext";
import { AuthProvider } from "@/context/AuthContext";
import { CustomerProvider } from "@/context/CustomerContext";
import { CartProvider } from "@/context/CartContext";
import { AccessibilityPanel } from "@/components/AccessibilityPanel";
import { initClickTracking, trackPageview } from "@/lib/analytics";
import { loadClosures } from "@/lib/hours";
import { loadPlanPrices } from "@/lib/plans";
import Home from "@/pages/Home";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import DevConsole from "@/pages/DevConsole";
import LandingGrooming from "@/pages/LandingGrooming";
import LandingSelf from "@/pages/LandingSelf";
import ShopPage from "@/pages/ShopPage";
import ProductPage from "@/pages/ProductPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import AccountPage from "@/pages/AccountPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import StaffApp from "@/pages/app/StaffApp";
import { useAuth } from "@/context/AuthContext";
import { CookieBanner } from "@/components/CookieBanner";
import { ThemeEffects } from "@/components/ThemeEffects";
import { Seo } from "@/components/Seo";
import { loadAnalytics, analyticsAllowed } from "@/lib/consent";

// Site chrome (themes, cookies, accessibility) — hidden inside the staff app (/app)
function SiteChrome() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/app")) return null;
  return (
    <>
      <ThemeEffects />
      <CookieBanner />
      <AccessibilityPanel />
    </>
  );
}

// Dev role gets the dark console; groomer role keeps the classic dashboard
function AdminGate() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  return (
    <>
      <Seo title="Care For Me | Admin" path="/admin" noindex />
      {user?.role === "dev" ? <DevConsole /> : <AdminDashboard />}
    </>
  );
}

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);
  return null;
}

function App() {
  useEffect(() => {
    initClickTracking();
    loadClosures();
    loadPlanPrices();
    if (analyticsAllowed()) loadAnalytics(); // returning visitor who already accepted
  }, []);

  return (
    <div className="App">
      <LanguageProvider>
        <AccessibilityProvider>
          <AuthProvider>
            <CustomerProvider>
              <CartProvider>
                <HelmetProvider>
                <BrowserRouter>
                  <RouteTracker />
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/landing/grooming" element={<LandingGrooming />} />
                    <Route path="/landing/self" element={<LandingSelf />} />
                    <Route path="/shop" element={<ShopPage />} />
                    <Route path="/shop/product/:id" element={<ProductPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/shop/checkout" element={<CheckoutPage />} />
                    <Route path="/account" element={<AccountPage />} />
                    <Route path="/account/reset" element={<ResetPasswordPage />} />
                    <Route path="/app" element={<StaffApp />} />
                    <Route path="/admin" element={<AdminGate />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                  </Routes>
                  <SiteChrome />
                  <Toaster position="top-center" richColors />
                </BrowserRouter>
                </HelmetProvider>
              </CartProvider>
            </CustomerProvider>
          </AuthProvider>
        </AccessibilityProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
